import { useState } from 'react'

import Box from '@totallymoney/ui/components/Box'
import Grid from '@totallymoney/ui/components/Grid'
import Heading from '@totallymoney/ui/components/Heading'
import LinkButton from '@totallymoney/ui/components/LinkButton'
import LinkWithChevron from '@totallymoney/ui/components/LinkWithChevron'
import theme from '@totallymoney/ui/theme'

import { ScoredCreditCardProduct } from '@appTypes/EligibilityApi.interface'

import CardPageContentBase from '@components/CardPageContentBase'
import CardPageImage from '@components/CardPageImage'
import CardPageItemProductDetail from '@components/CardPageItemProductDetail'
import CardPageProductLists from '@components/CardPageProductLists'
import CreditCardContent from '@components/CreditCardContent'
import RatesAndFees from '@components/RatesAndFees'
import RepresentativeExample from '@components/RepresentativeExample'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'

import { getLoginUrl } from '@routes/UnscoredTableRoute/utils/getLoginUrl'
import { getRedirectAccountUrl } from '@routes/UnscoredTableRoute/utils/getRedirectAccountUrl'

import { getIsDormantScoreIdJourney } from '@utils/getIsDormantScoreIdJourney'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'
import { getSanitisedCardName } from '@utils/getSanitisedCardName'
import { getUnscoredCategoryPath } from '@utils/getUnscoredCategoryPath'

import * as Layout from './components/UnscoredCardLayout'

interface UnscoredCardProps {
    unscoredCard: ScoredCreditCardProduct
}

enum ModalType {
    RepresentativeExample = 'RepresentativeExample',
}

const UnscoredCard = ({ unscoredCard }: UnscoredCardProps) => {
    const logEvents = useLogEvents()

    const {
        appQueryState,
        getQueryStringWithAppState,
        getRouteUrl,
        goToRouteWithAppState,
    } = useAppQueryState()

    const [showModal, setShowModal] = useState<ModalType | null>(null)

    const formHref =
        appQueryState.mediaSource === 'VTSRTG'
            ? getLoginUrl()
            : getRedirectAccountUrl({
                  queryObject: {
                      category:
                          appQueryState.category === 'all-cards'
                              ? ''
                              : appQueryState.category,
                      card: getSanitisedCardName(unscoredCard.name),
                      ccid: unscoredCard.productCode,
                  },
                  queryString: getQueryStringWithAppState(),
              })

    const onCheckEligibilityClick = (
        event: React.SyntheticEvent<Element, Event>
    ) => {
        event.preventDefault()

        if (getIsDormantScoreIdJourney(appQueryState)) {
            return goToRouteWithAppState(appQueryState.category!, {
                queryParameters: {
                    scoreId: appQueryState.dormantScoreId,
                    customerId: appQueryState.customerId,
                },
            })
        }

        logEvents.logCheckEligibilityEvent({
            trigger: 'row',
            category: appQueryState.categoryId!,
            productCode: unscoredCard.productCode,
            rowNumber: 0,
        })

        window.location.href = formHref
    }

    const categoryPath = getUnscoredCategoryPath(appQueryState.categoryId!)

    return (
        <Grid>
            <Layout.BackLink>
                <LinkWithChevron
                    href={getRouteUrl(categoryPath)}
                    variant="100"
                    direction="left"
                    onClick={() => {
                        event!.preventDefault()
                        goToRouteWithAppState(categoryPath)
                    }}
                >
                    Back to cards
                </LinkWithChevron>
            </Layout.BackLink>
            <Layout.PageHeading>
                <Heading as="h1" variant="60" color="light">
                    {unscoredCard.name}
                </Heading>
            </Layout.PageHeading>
            <Layout.ItemWrapper>
                <Layout.ItemDetails>
                    <Layout.ItemImage>
                        <CardPageImage
                            name={unscoredCard.name}
                            cmsImageUrl={unscoredCard.imageUrlLarge}
                            isPortraitImage={getIsProductPortraitImage(
                                unscoredCard.productCode
                            )}
                        />
                    </Layout.ItemImage>
                    <Layout.ItemContent>
                        <Box pb="space30">
                            <CardPageContentBase>
                                <CreditCardContent
                                    category={appQueryState.categoryId!}
                                    creditCard={unscoredCard}
                                    renderProductDetail={detail => (
                                        <CardPageItemProductDetail
                                            {...detail}
                                        />
                                    )}
                                />
                            </CardPageContentBase>
                        </Box>

                        <RepresentativeExample
                            item={unscoredCard}
                            onTooltipPress={() =>
                                setShowModal(ModalType.RepresentativeExample)
                            }
                        />
                    </Layout.ItemContent>
                </Layout.ItemDetails>
                <Layout.ItemButtons>
                    <Box
                        position={['fixed', 'relative']}
                        bottom={0}
                        left={0}
                        width={['100%', '350px', 'auto']}
                        backgroundColor="white"
                        borderTop={[`solid 1px ${theme.indigo100}`, 0]}
                    >
                        <Box p={['space30', 0]}>
                            <LinkButton
                                href={formHref}
                                variant="primary"
                                text="Check my eligibility"
                                onClick={onCheckEligibilityClick}
                                data-gtm-event="eligibility-table-cardSpecific"
                                data-testid="eligibility-table-cardSpecific"
                            />
                        </Box>
                    </Box>
                </Layout.ItemButtons>
            </Layout.ItemWrapper>
            <Box gridColumn="1/13" pb="space50">
                <CardPageProductLists card={unscoredCard} />
            </Box>
            <Box gridColumn="1/13" pb="space50">
                <Box mb="space20">
                    <Heading as="h2" variant="60">
                        Rates and Fees
                    </Heading>
                </Box>
                <RatesAndFees card={unscoredCard} />
            </Box>
            <RepresentativeExampleModal
                showModal={showModal == ModalType.RepresentativeExample}
                onModalClose={() => setShowModal(null)}
            />
        </Grid>
    )
}

export default UnscoredCard
